import React, { useCallback, useEffect, useRef, useState } from "react";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import { useFormContext } from "react-hook-form";

import clsx from "clsx";
import { animated, useSpring } from "@react-spring/web";
import { styled } from "@mui/material/styles";
import api from "service/api";
import { toast } from "react-toastify";
import { useTreeItem2 } from "@mui/x-tree-view/useTreeItem2";
import {
  TreeItem2IconContainer,
  TreeItem2Label,
} from "@mui/x-tree-view/TreeItem2";
import { TreeItem2Icon } from "@mui/x-tree-view/TreeItem2Icon";
import { TreeItem2Provider } from "@mui/x-tree-view/TreeItem2Provider";
import { TreeItem2DragAndDropOverlay } from "@mui/x-tree-view/TreeItem2DragAndDropOverlay";
import { useTreeViewApiRef } from "@mui/x-tree-view";

import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import FolderRounded from "@mui/icons-material/FolderRounded";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import { CustomTreeItemContent, StyledTreeItemRoot } from "./style";

const AnimatedCollapse = animated(Collapse);

function TransitionComponent(props) {
  const style = useSpring({
    to: {
      opacity: props.in ? 1 : 0,
      transform: `translate3d(0,${props.in ? 0 : 20}px,0)`,
    },
  });

  return <AnimatedCollapse style={style} {...props} />;
}

const StyledTreeItemLabelText = styled(Typography)({
  color: "inherit",
  fontWeight: 500,
});

function CustomLabel({ icon: Icon, expandable, children, ...other }) {
  return (
    <TreeItem2Label
      {...other}
      sx={{
        display: "flex",
        alignItems: "center",
      }}
    >
      {Icon && (
        <Box
          component={Icon}
          className="labelIcon"
          color="inherit"
          sx={{ mr: 1, fontSize: "1.2rem" }}
        />
      )}

      <StyledTreeItemLabelText variant="body2">
        {children}
      </StyledTreeItemLabelText>
    </TreeItem2Label>
  );
}

const isExpandable = (reactChildren) => {
  if (Array.isArray(reactChildren)) {
    return reactChildren.length > 0 && reactChildren.some(isExpandable);
  }
  return Boolean(reactChildren);
};

const CustomTreeItem = React.forwardRef(function CustomTreeItem(props, ref) {
  const { id, itemId, label, disabled, children, ...other } = props;

  const {
    getRootProps,
    getContentProps,
    getIconContainerProps,
    getLabelProps,
    getGroupTransitionProps,
    getDragAndDropOverlayProps,
    status,
    publicAPI,
  } = useTreeItem2({ id, itemId, children, label, disabled, rootRef: ref });

  const item = publicAPI.getItem(itemId);
  const expandable = isExpandable(children);
  let icon;
  if (expandable) {
    icon = FolderRounded;
  } else if (item.fileType) {
    icon = FolderOpenIcon;
  }

  return (
    <TreeItem2Provider itemId={itemId}>
      <StyledTreeItemRoot {...getRootProps(other)}>
        <CustomTreeItemContent
          {...getContentProps({
            className: clsx("content", {
              "Mui-expanded": status.expanded,
              "Mui-selected": status.selected,
              "Mui-focused": status.focused,
              "Mui-disabled": status.disabled,
            }),
          })}
        >
          <TreeItem2IconContainer {...getIconContainerProps()}>
            <TreeItem2Icon status={status} />
          </TreeItem2IconContainer>
          <CustomLabel
            {...getLabelProps({
              icon,
              expandable: expandable && status.expanded,
            })}
          />
          <TreeItem2DragAndDropOverlay {...getDragAndDropOverlayProps()} />
        </CustomTreeItemContent>
        {children && <TransitionComponent {...getGroupTransitionProps()} />}
      </StyledTreeItemRoot>
    </TreeItem2Provider>
  );
});

export default function LazyAudioFolderTree({ isMulti = false }) {
  const { setValue, getValues } = useFormContext();
  const [loading, setLoading] = useState(false);
  const [cachedFolders, setCachedFolders] = useState(new Map());
  const [data, setData] = useState([]);
  const [expandedItems, setExpandedItems] = useState([]);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const apiRef = useTreeViewApiRef();
  const containerRef = useRef();
  const cachedFoldersRef = useRef(new Map());
  const [scrollTop, setScrollTop] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [loadingIds, setLoadingIds] = useState(new Set());

  const getFolderHierarchy = useCallback(async (folderId) => {
    try {
      const response = await api.get(
        `/event-audios/folders/${folderId}/hierarchy`,
      );
      if (response.status === 200) {
        if (response.data.length === 0) {
          toast.warn(
            `Algumas pastas copiadas foram removidas ou não estão mais acessíveis..`,
          );
        }
        return response.data || [];
      }
    } catch (error) {
      console.warn(
        `Não foi possível buscar hierarquia da pasta ${folderId}:`,
        error,
      );
    }
    return [];
  }, []);

  const updateChildren = useCallback((nodes, parentId, children) => {
    return nodes.map((node) => {
      if (node.id === parentId) {
        return { ...node, children };
      }
      if (node.children) {
        return {
          ...node,
          children: updateChildren(node.children, parentId, children),
        };
      }
      return node;
    });
  }, []);

  const fetchFolders = useCallback(
    async (id, cacheToUse = null) => {
      const currentCache = cacheToUse || cachedFoldersRef.current;

      if (currentCache.has(id)) {
        return currentCache;
      }

      if (loadingIds.has(id)) {
        return currentCache;
      }

      setLoadingIds((prev) => new Set([...prev, id]));
      setLoading(true);

      try {
        const response = await api.get(`/event-audios/folders?folderId=${id}`);

        if (response.status !== 200) {
          toast.error("Houve um problema ao tentar recuperar os diretórios");
          return currentCache;
        }

        const fetchedFolders = response.data.map((item) => ({
          id: item.id,
          label: item.name,
          dateFormat: item.dateFormat ?? null,
          children: [],
          fileType: "folder",
        }));

        const newCache = new Map(currentCache);
        newCache.set(id, fetchedFolders);
        if (!cacheToUse) {
          setData((prev) => updateChildren(prev, id, fetchedFolders));
          setCachedFolders(newCache);
        }

        return newCache;
      } catch (e) {
        console.log(e);
        toast.error("Houve um problema ao tentar recuperar os diretórios");
        return currentCache;
      } finally {
        setLoadingIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
        setLoading(false);
      }
    },
    [updateChildren],
  );

  const expandHierarchyForCopy = useCallback(
    async (selectedIds) => {
      if (!selectedIds.length) return [];

      const allParentIds = new Set();

      for (const folderId of selectedIds) {
        const hierarchy = await getFolderHierarchy(folderId);

        hierarchy.forEach((folder) => {
          if (folder.parentFolderId) {
            allParentIds.add(folder.parentFolderId);
          }
          if (!selectedIds.includes(folder.id)) {
            allParentIds.add(folder.id);
          }
        });
      }

      return Array.from(allParentIds);
    },
    [getFolderHierarchy],
  );

  const loadRequiredFolders = useCallback(
    async (parentIds, initialCache) => {
      let currentCache = initialCache;

      for (const parentId of parentIds) {
        currentCache = await fetchFolders(parentId, currentCache);
      }

      return currentCache;
    },
    [fetchFolders],
  );

  const expandCachedItems = useCallback((nodes, items, cached) => {
    return nodes.map((node) => {
      if (items.includes(node.id)) {
        node.expanded = true;
      }

      if (cached.has(node.id)) {
        node.children = cached.get(node.id);
        node.children = expandCachedItems(node.children, items, cached);
      }

      return node;
    });
  }, []);

  const fetchRootFolders = useCallback(
    async (cached, expandedItemsToUse) => {
      setLoading(true);
      try {
        const response = await api.get(`/event-audios/folders`);

        if (response.status !== 200) {
          toast.error(
            "Houve um problema ao tentar recuperar os diretórios raízes.",
          );
          return;
        }
        const fetchedFolders = response.data.map((item) => ({
          id: item.id,
          label: item.name,
          dateFormat: item.dateFormat ?? null,
          children: [],
          fileType: "folder",
        }));

        setValue("CachedRootFolders", fetchedFolders);

        const updatedData = expandCachedItems(
          fetchedFolders,
          expandedItemsToUse,
          cached,
        );
        setData(updatedData);
      } catch (e) {
        console.log(e);
        toast.error(
          "Houve um problema ao tentar recuperar os diretórios raízes.",
        );
      } finally {
        setLoading(false);
      }
    },
    [expandCachedItems, setValue],
  );

  useEffect(() => {
    if (isInitialized) return;

    const initializeComponent = async () => {
      const cachedFoldersData = getValues("CachedFolders") ?? new Map();
      const cachedExpandedItems = getValues("CachedExpandedItems") ?? [];
      const cachedSelectedItems = getValues("CachedSelectedItems") ?? [];
      const isCopy = getValues("IsCopy") ?? false;

      setCachedFolders(cachedFoldersData);
      setSelectedFolders(cachedSelectedItems);
      setValue("folders", cachedSelectedItems);

      if (isCopy && cachedSelectedItems.length > 0) {
        try {
          const parentIds = await expandHierarchyForCopy(cachedSelectedItems);

          const finalCache = await loadRequiredFolders(
            parentIds,
            cachedFoldersData,
          );
          const allExpandedItems = [
            ...new Set([...cachedExpandedItems, ...parentIds]),
          ];

          setExpandedItems(allExpandedItems);
          setCachedFolders(finalCache);

          await fetchRootFolders(finalCache, allExpandedItems);
        } catch (error) {
          setExpandedItems(cachedExpandedItems);
          await fetchRootFolders(cachedFoldersData, cachedExpandedItems);
        }
      } else {
        setExpandedItems(cachedExpandedItems);
        await fetchRootFolders(cachedFoldersData, cachedExpandedItems);
      }

      setIsInitialized(true);
    };

    initializeComponent();

    const handleScroll = () => {
      if (containerRef.current) {
        setScrollTop(containerRef.current.scrollTop);
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [
    isInitialized,
    expandHierarchyForCopy,
    loadRequiredFolders,
    fetchRootFolders,
    setValue,
    getValues,
  ]);

  useEffect(() => {
    if (isInitialized) {
      setValue("CachedFolders", cachedFolders);
    }
  }, [cachedFolders, setValue, isInitialized]);

  useEffect(() => {
    if (isInitialized) {
      setValue("CachedExpandedItems", expandedItems);
    }
  }, [expandedItems, setValue, isInitialized]);

  useEffect(() => {
    if (isInitialized) {
      setValue("CachedSelectedItems", selectedFolders);
    }
  }, [selectedFolders, setValue, isInitialized]);

  useEffect(() => {
    if (containerRef.current) {
      if (loading) {
        containerRef.current.style.overflow = "hidden";
      } else {
        containerRef.current.style.overflow = "auto";
      }
    }
  }, [loading]);

  const handleNodeSelect = useCallback(
    (_, id) => {
      apiRef.current.focusItem(_, null);
      document.activeElement?.blur();

      if (selectedFolders.includes(id)) {
        const updatedSelectedFolders = selectedFolders.filter(
          (selectedId) => selectedId !== id,
        );
        setSelectedFolders(updatedSelectedFolders);
        setValue("folders", updatedSelectedFolders);
        return;
      }
      if (expandedItems.includes(id)) {
        setExpandedItems((prev) => prev.filter((item) => item !== id));
        return;
      }
      if (cachedFolders.has(id)) {
        const cachedData = cachedFolders.get(id);
        setData((prev) => updateChildren(prev, id, cachedData));
        setExpandedItems((prev) => [...new Set([...prev, id])]);
        if (!cachedData || cachedData.length === 0) {
          const updatedSelectedFolders = [...selectedFolders, id];
          setSelectedFolders(updatedSelectedFolders);
          setValue("folders", updatedSelectedFolders);
        }
        return;
      }

      if (loadingIds.has(id)) {
        return;
      }

      const loadAndExpand = async () => {
        try {
          const newCache = await fetchFolders(id);
          const folderData = newCache.get(id) || [];

          setExpandedItems((prev) => [...new Set([...prev, id])]);

          if (folderData.length === 0) {
            const hierarchy = await getFolderHierarchy(id);
            if (hierarchy.length > 0) {
              const updatedSelectedFolders = [...selectedFolders, id];
              setSelectedFolders(updatedSelectedFolders);
              setValue("folders", updatedSelectedFolders);
            }
          }
        } catch (error) {
          console.error("Erro ao carregar pasta:", error);
        }
      };

      loadAndExpand();
    },
    [
      selectedFolders,
      cachedFolders,
      expandedItems,
      setValue,
      fetchFolders,
      apiRef,
      updateChildren,
      loadingIds,
    ],
  );

  useEffect(() => {
    cachedFoldersRef.current = cachedFolders;
  }, [cachedFolders]);

  return (
    <div
      ref={containerRef}
      className="styled-scroll"
      style={{
        position: "relative",
        height: "340px",
        overflowY: "scroll",
      }}
    >
      <RichTreeView
        apiRef={apiRef}
        items={data}
        expandedItems={expandedItems}
        selectedItems={selectedFolders}
        onItemClick={handleNodeSelect}
        multiSelect={isMulti}
        sx={{
          height: "fit-content",
          flexGrow: 1,
          maxWidth: 400,
          overflowY: "auto",
        }}
        slots={{ item: CustomTreeItem }}
      />
      <Backdrop
        open={loading}
        sx={{
          color: "#404040",
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          position: "absolute",
          top: `${scrollTop}px`,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
}
